import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, CircularProgress, MenuItem, Select, FormControl,
    InputLabel, Stack, Typography, Avatar, Alert, Card, Button
} from '@mui/material';
import { CheckCircle, WavingHand, CameraAlt, LocationOn } from '@mui/icons-material';
import toast, { Toaster } from 'react-hot-toast';
import configService from '../services/config';
import { useSelector } from 'react-redux';
import { useAttendance } from '../context/AttendanceContext';

// --- LEAFLET MAP IMPORTS ---
import { MapContainer, TileLayer, Marker, Polygon } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png')
});
// ---------------------------

const STEPS = { IDLE: 'idle', CAMERA: 'camera', PREVIEW: 'preview', DONE: 'done' };

export default function PunchIn() {
    const [step, setStep] = useState(STEPS.IDLE);
    const [now, setNow] = useState(new Date());
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [photo, setPhoto] = useState(null);
    const [location, setLocation] = useState(null);
    const [locationLabel, setLocationLabel] = useState('');
    const [locationLoading, setLocationLoading] = useState(false);
    const [locationAccuracy, setLocationAccuracy] = useState(null); // in meters
    const [locationTimeout, setLocationTimeout] = useState(false); // true if we gave up after 60s
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // Attendance States
    const [statusData, setStatusData] = useState({ in: false, out: false, in_time: null, out_time: null });
    const [stream, setStream] = useState(null);

    const { company_id, userId } = useSelector(state => state.globalVariables);
    const { punchIn: syncPunchIn } = useAttendance();
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const watchIdRef = useRef(null);
    const timeoutIdRef = useRef(null);
    const goodFixAcquiredRef = useRef(false);
    const hasAnyFixRef = useRef(false);

    // 1. Clock Timer
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    // 2. Initial Data Fetch
    const checkStatusAndLoadSites = useCallback(async () => {
        try {
            const [statusRes, sites] = await Promise.all([
                configService.getTodayStatus({ employee_id: userId, company_id: company_id }),
                configService.get_customer({ company_id: company_id })
            ]);

            if (statusRes.status) {
                setStatusData(statusRes.data);
            }
            setCustomers(sites || []);
        } catch (err) {
            toast.error("Data loading failed");
        } finally {
            setInitialLoading(false);
        }
    }, [userId, company_id]);

    useEffect(() => {
        checkStatusAndLoadSites();
    }, [checkStatusAndLoadSites]);

    // 3. Camera Controls
    const startCamera = async () => {
        setStep(STEPS.CAMERA);
        try {
            const s = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'user', width: 640, height: 480 }
            });
            setStream(s);
            if (videoRef.current) videoRef.current.srcObject = s;
            // Start fetching location early
            fetchLocation();
        } catch (err) {
            toast.error('Camera access denied!');
            setStep(STEPS.IDLE);
        }
    };

    const capturePhoto = () => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.scale(-1, 1);
        ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
        setPhoto(canvas.toDataURL('image/jpeg', 0.85));
        if (stream) stream.getTracks().forEach(t => t.stop());
        setStep(STEPS.PREVIEW);
        // If location not yet resolved, ensure it's still trying
        if (!location && !locationLoading) {
            fetchLocation();
        }
    };

    // ---------- FIXED LOCATION LOGIC – EXACT GPS ----------
    const reverseGeocode = (lat, lng) => {
        fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=jsonv2&addressdetails=1&zoom=18`)
            .then(res => res.json())
            .then(data => {
                const a = data?.address || {};

                // Build a Google-Maps-style line: nearest place, locality, city, state, pincode
                const nearest = a.amenity || a.building || a.shop || a.office || a.road || a.neighbourhood;
                const locality = a.suburb || a.neighbourhood || a.village || a.town;
                const city = a.city || a.town || a.county;
                const state = a.state;
                const pincode = a.postcode;

                const parts = [];
                if (nearest) parts.push(nearest);
                if (locality && locality !== nearest) parts.push(locality);
                if (city && city !== locality) parts.push(city);
                if (state) parts.push(state);
                if (pincode) parts.push(pincode);

                const label = parts.length ? parts.join(', ') : data?.display_name;
                setLocationLabel(label || `${lat.toFixed(4)}, ${lng.toFixed(4)}`);
            })
            .catch(() => setLocationLabel(`${lat.toFixed(4)}, ${lng.toFixed(4)}`));
    };

    const stopLocationWatch = () => {
        if (watchIdRef.current) {
            navigator.geolocation.clearWatch(watchIdRef.current);
            watchIdRef.current = null;
        }
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    };

    const fetchLocation = () => {
        // Clean up any previous watch
        stopLocationWatch();
        goodFixAcquiredRef.current = false;
        hasAnyFixRef.current = false;
        setLocationTimeout(false);
        setLocationAccuracy(null);

        if (!navigator.geolocation) {
            toast.error("Geolocation not supported by your browser.");
            return;
        }

        setLocationLoading(true);

        let bestAccuracySoFar = Infinity;

        // Start watching
        watchIdRef.current = navigator.geolocation.watchPosition(
            (pos) => {
                const { latitude, longitude, accuracy } = pos.coords;
                setLocationAccuracy(accuracy);

                // Show the first/best fix we get on the map immediately so the UI
                // isn't stuck blank waiting for a perfect <=50m fix.
                if (accuracy < bestAccuracySoFar) {
                    bestAccuracySoFar = accuracy;
                    hasAnyFixRef.current = true;
                    setLocation({ lat: latitude, lng: longitude });
                    reverseGeocode(latitude, longitude);
                    // Once we have any reading, stop blocking the UI with the
                    // loading overlay — map is now visible, refines quietly.
                    setLocationLoading(false);
                }

                // Treat <=50m as a "good" fix and stop watching to save battery.
                if (accuracy <= 50) {
                    if (!goodFixAcquiredRef.current) {
                        goodFixAcquiredRef.current = true;
                        setLocationTimeout(false);
                        toast.success(`GPS locked (accuracy ${Math.round(accuracy)}m)`);
                        stopLocationWatch();
                    }
                }
            },
            (error) => {
                console.error("GPS watch error:", error);
                toast.error(`GPS error: ${error.message}. Please enable GPS.`);
                // Only fully fail out if we never got even a rough fix.
                if (!hasAnyFixRef.current) {
                    setLocationLoading(false);
                    setLocationTimeout(true);
                }
                stopLocationWatch();
            },
            {
                enableHighAccuracy: true,
                timeout: 20000,
                maximumAge: 0,
            }
        );

        // Safety timeout – if after 25 seconds we still have no fix at all, give up
        // and show options instead of spinning forever.
        timeoutIdRef.current = setTimeout(() => {
            if (!goodFixAcquiredRef.current) {
                if (!hasAnyFixRef.current) {
                    setLocationLoading(false);
                    setLocationTimeout(true);
                    toast.error("Could not get GPS fix. Try moving outdoors or retry.");
                } else {
                    // Rough fix already shown on map — quietly stop the background watch.
                }
                stopLocationWatch();
            }
        }, 25000);
    };

    // Fallback: use low-accuracy location manually (user clicks "Use Current")
    const useLowAccuracyLocation = () => {
        // Get the last known position from the watch (if any) or use a one-time getCurrentPosition
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const { latitude, longitude, accuracy } = pos.coords;
                hasAnyFixRef.current = true;
                setLocation({ lat: latitude, lng: longitude });
                reverseGeocode(latitude, longitude);
                setLocationLoading(false);
                setLocationTimeout(false);
                toast.success(`Using low-accuracy location (±${Math.round(accuracy)}m). Consider moving outdoors.`);
            },
            (err) => {
                toast.error("Could not get any location. Please enable GPS.");
            },
            { enableHighAccuracy: false, timeout: 5000 }
        );
    };
    // ------------------------------------------

    // 5. Submit Logic
    const handleSubmit = async () => {
        setLoading(true);
        try {
            const blob = await fetch(photo).then(res => res.blob());
            const formData = new FormData();
            formData.append('site_id', selectedCustomer);
            formData.append('company_id', company_id);
            formData.append('type', "IN");
            formData.append('employee_id', userId);
            formData.append('image', blob, 'punch_in.jpg');
            formData.append('latitude', location?.lat || 0);
            formData.append('longitude', location?.lng || 0);
            formData.append('place', locationLabel);

            const response = await configService.punch(formData);
            if (response.status) {
                syncPunchIn({ photo, location });
                toast.success("Punch In Successful!");
                setStep(STEPS.DONE);
                checkStatusAndLoadSites();
            }
        } catch (err) {
            toast.error("Submission failed!");
        } finally {
            setLoading(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopLocationWatch();
        };
    }, []);

    const fmtTime = (dateStr) => dateStr ? new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : "--:--";
    const fmtDate = (d) => d.toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

    if (initialLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;

    const stopStream = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    };

    const isOut = statusData.in;

    return (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', minHeight: '80vh', alignItems: 'center', bgcolor: '#f4f6f8' }}>
            <Toaster />
            <Card sx={{
                maxWidth: 450, width: '100%', bgcolor: '#ffffff', color: '#1e293b',
                p: 4, borderRadius: 6, boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                textAlign: 'center', border: '1px solid rgba(0,0,0,0.05)'
            }}>

                {/* Step: DONE or Already Punched In */}
                {(isOut || step === STEPS.DONE) ? (
                    <Stack spacing={3} alignItems="center">
                        <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                            <CircularProgress variant="determinate" value={100} size={180} thickness={2} sx={{ color: '#1976d2' }} />
                            <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b' }}>{fmtTime(statusData.in_time)}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>PUNCHED IN</Typography>
                            </Box>
                        </Box>
                        <CheckCircle sx={{ fontSize: 60, color: '#2e7d32' }} />
                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#1e293b' }}>You Are Working At</Typography>
                        <Alert icon={<WavingHand sx={{ color: '#1976d2' }} />} severity="success" sx={{ bgcolor: '#edf7ed', color: '#1e4620', width: '100%', border: '1px solid #c8e6c9' }}>
                            Have a productive day ahead!
                        </Alert>
                        <Button fullWidth variant="contained" disabled sx={{ py: 2, borderRadius: 3, bgcolor: '#f1f5f9', color: '#94a3b8 !important' }}>Already On Duty</Button>
                    </Stack>
                ) : (
                    /* Step: IDLE View */
                    <Stack spacing={3}>
                        {step === STEPS.IDLE && (
                            <>
                                <Box sx={{ position: 'relative', display: 'inline-flex', alignSelf: 'center' }}>
                                    <CircularProgress variant="determinate" value={100} size={200} thickness={1.5} sx={{ color: '#1976d2' }} />
                                    <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                        <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'monospace', color: '#1e293b' }}>
                                            {now.toLocaleTimeString([], { hour12: false })}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b', letterSpacing: 2, fontWeight: 600 }}>
                                            {fmtDate(now).toUpperCase()}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ textAlign: 'left', mt: 2 }}>
                                    <FormControl fullWidth variant="outlined" sx={{ bgcolor: '#f8fafc', borderRadius: 2 }}>
                                        <InputLabel sx={{ color: '#64748b' }}>Work Site Selection</InputLabel>
                                        <Select
                                            value={selectedCustomer}
                                            label="Work Site Selection"
                                            onChange={(e) => setSelectedCustomer(e.target.value)}
                                            sx={{ color: '#1e293b', ".MuiOutlinedInput-notchedOutline": { borderColor: '#e2e8f0' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' } }}
                                        >
                                            {customers
                                                ?.filter(c => c.type === "Standard")
                                                ?.map((c) => (
                                                    <MenuItem key={c.id} value={c.id}>
                                                        {c.fname}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Alert severity="info" icon={<LocationOn fontSize="small" />} sx={{ bgcolor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', textAlign: 'left' }}>
                                    Selfie & Location are required to mark attendance.
                                </Alert>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={!selectedCustomer}
                                    onClick={startCamera}
                                    startIcon={<CameraAlt />}
                                    sx={{
                                        py: 2, borderRadius: 4, bgcolor: '#1976d2', fontWeight: 'bold', color: '#ffffff',
                                        boxShadow: '0 8px 16px rgba(25, 118, 210, 0.15)',
                                        '&:hover': { bgcolor: '#1565c0' },
                                        '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
                                    }}
                                >
                                    Start Punch In
                                </Button>
                            </>
                        )}

                        {/* Camera View */}
                        {step === STEPS.CAMERA && (
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2,
                                }}
                            >
                                {/* Camera Box */}
                                <Box
                                    sx={{
                                        borderRadius: 6,
                                        overflow: "hidden",
                                        position: "relative",
                                        border: "2px solid #1976d2",
                                        width: "100%",
                                        aspectRatio: { xs: "3/4", md: "16/9" },
                                        bgcolor: "#000",
                                    }}
                                >
                                    <video
                                        ref={videoRef}
                                        autoPlay
                                        playsInline
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            transform: "scaleX(-1)",
                                            display: "block",
                                        }}
                                    />

                                    {/* Optional: Face Guide */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            transform: "translate(-50%, -50%)",
                                            width: "65%",
                                            height: "55%",
                                            border: "2px dashed rgba(255,255,255,0.6)",
                                            borderRadius: "50%",
                                            pointerEvents: "none",
                                        }}
                                    />
                                </Box>

                                {/* Controls BELOW */}
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 1.5,
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        sx={{
                                            bgcolor: "#1976d2",
                                            color: '#ffffff',
                                            borderRadius: 10,
                                            px: 6,
                                            py: 1.3,
                                            fontWeight: "bold",
                                            '&:hover': { bgcolor: '#1565c0' }
                                        }}
                                        onClick={capturePhoto}
                                    >
                                        Capture Now
                                    </Button>

                                    <Button
                                        variant="text"
                                        sx={{ color: '#64748b', textTransform: "none", fontWeight: 600, '&:hover': { bgcolor: '#f1f5f9' } }}
                                        onClick={() => {
                                            stopStream();
                                            setStep(STEPS.IDLE);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* Preview View with LIVE MAP */}
                        {step === STEPS.PREVIEW && (
                            <Stack spacing={2} sx={{ width: '100%', alignItems: 'center' }}>
                                {/* User Selfie */}
                                <Avatar src={photo} sx={{ width: 100, height: 100, border: '3px solid #1976d2' }} />

                                {/* LIVE MAP SECTION */}
                                <Box sx={{
                                    height: 220,
                                    width: '100%',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    border: '2px solid #e2e8f0',
                                    bgcolor: '#fff',
                                    position: 'relative'
                                }}>
                                    {locationLoading && (
                                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(2px)', zIndex: 10 }}>
                                            <CircularProgress size={28} thickness={4} sx={{ color: '#1976d2' }} />
                                            <Typography variant="caption" sx={{ color: '#1e293b', mt: 1, fontWeight: 600 }}>
                                                Locating you…
                                            </Typography>
                                        </Box>
                                    )}
                                    {locationTimeout && (
                                        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.95)', zIndex: 10 }}>
                                            <Typography sx={{ color: '#b45309', fontWeight: 'bold' }}>GPS signal weak</Typography>
                                            <Button variant="outlined" sx={{ mt: 1, color: '#1976d2', borderColor: '#1976d2' }} onClick={fetchLocation}>
                                                Retry GPS
                                            </Button>
                                            <Button variant="text" sx={{ mt: 1, color: '#1d4ed8', fontWeight: 600 }} onClick={useLowAccuracyLocation}>
                                                Use current (low accuracy)
                                            </Button>
                                        </Box>
                                    )}
                                    {location ? (
                                        <MapContainer
                                            center={[location.lat, location.lng]}
                                            zoom={14}
                                            style={{ height: '100%', width: '100%' }}
                                            zoomControl={false}
                                        >
                                            <TileLayer
                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                attribution='&copy; OpenStreetMap'
                                            />

                                            {/* Blue Marker on User Location */}
                                            <Marker position={[location.lat, location.lng]} />

                                            {/* RED DOTTED BOUNDARY (Geofence) */}
                                            <Polygon
                                                positions={[
                                                    [location.lat + 0.015, location.lng - 0.015],
                                                    [location.lat + 0.015, location.lng + 0.015],
                                                    [location.lat - 0.015, location.lng + 0.015],
                                                    [location.lat - 0.015, location.lng - 0.015],
                                                ]}
                                                pathOptions={{
                                                    color: '#d32f2f',
                                                    dashArray: '5, 8',
                                                    fillOpacity: 0.05,
                                                    weight: 2
                                                }}
                                            />
                                        </MapContainer>
                                    ) : (
                                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
                                            <Typography sx={{ color: '#64748b' }}>No location yet</Typography>
                                        </Box>
                                    )}
                                </Box>

                                <Typography variant="caption" sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, fontWeight: 600 }}>
                                    <LocationOn fontSize="small" /> {locationLabel || (locationLoading ? "Detecting location..." : "No location available")}
                                    {locationAccuracy && !locationLoading && ` (accuracy ${Math.round(locationAccuracy)}m)`}
                                </Typography>

                                <Stack direction="row" spacing={2} sx={{ width: '100%', pt: 1 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        sx={{ color: '#64748b', borderColor: '#cbd5e1', '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' } }}
                                        onClick={() => {
                                            setPhoto(null);
                                            startCamera();
                                        }}
                                    >
                                        Retake
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleSubmit}
                                        disabled={loading || !location || locationLoading}
                                        sx={{ bgcolor: '#2e7d32', color: '#ffffff', '&:hover': { bgcolor: '#1b5e20' }, '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' } }}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : "Submit Attendance"}
                                    </Button>
                                </Stack>
                                {!location && !locationLoading && !locationTimeout && (
                                    <Button variant="text" sx={{ color: '#1976d2', fontWeight: 600 }} onClick={fetchLocation}>
                                        Retry Location
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                )}
            </Card>
            <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
    );
}